import { DateTime } from "luxon";
import * as msal from "@azure/msal-browser";

export const getMsalInstance = async () => {
  const instance = new msal.PublicClientApplication({
    auth: {
      clientId: process.env.REACT_APP_MS_CLIENT_ID,
      authority: process.env.REACT_APP_MS_AUTH_URL,
      redirectUri: window.location.origin,
    },
    cache: {
      cacheLocation: "localStorage",
    },
  });

  await instance.initialize();

  return instance;
};

// Validate token expiration
export const isTokenValid = (session) => {
  if (!session?.token) {
    return false;
  }
  
  const tokenExpire = DateTime.fromSeconds(session.exp);
  return tokenExpire.isValid && tokenExpire >= DateTime.now();
};

/**
 * Generates a fake promise to simulate loading times
 * @param {Integer} seconds Number os seconds to delay
 * @param data Response data to be returned after seconds
 * @returns
 */
export const fakePromise = (seconds, data, error) => {
  return new Promise((resolve, reject) =>
    setTimeout(() => {
      if (error) {
        reject(new Error(error));
      } else {
        resolve(data);
      }
    }, seconds * 1000),
  );
};

export const generateAddress = (line1, line2, city, province, postalCode) => {
  let address = line1 || "";
  address = line2 ? `${address} ${line2}` : address;
  address = city ? `${address}${address && ","} ${city}` : address;
  address = province ? `${address}${address && ","} ${province}` : address;

  return postalCode ? `${address} ${postalCode}` : address;
};

/**
 * Takes in an ISO date string, converts to a DateTime object. Converts to desired format if provided
 * @param {string} isoDateTime original iso date
 * @param {string} format output format: date, fvDate, time, dateTime or custom
 * @param {object} customFormat an object containing the custom format. Details:
 * https://tc39.es/ecma402/#datetimeformat-objects:~:text=Styles%20Record.-,11.2.3.1%20DateTime%20Format%20Records,-Each%20DateTime%20Format
 * @returns DateTime object or formatted date and/or time string
 */
export const formatIsoDateTime = (isoDateTime, format, customFormat) => {
  if (!isoDateTime) {
    return "";
  }

  let dateTime = null;

  if (isoDateTime.includes("Z") || isoDateTime.includes("+00:00")) {
    dateTime = DateTime.fromISO(isoDateTime, { zone: "utc" });
  } else {
    dateTime = DateTime.fromISO(isoDateTime);
  }

  if (dateTime.invalid) {
    return "";
  }

  if (customFormat) {
    return dateTime.toLocaleString(customFormat);
  }

  if (format) {
    if (format === "date") {
      return dateTime.toLocaleString(DateTime.DATE_MED);
    }

    //FV uses the US standard (mm/dd/yyyy)
    if (format === "fvDate") {
      return dateTime.setLocale("en-US").toLocaleString({ day: "2-digit", month: "2-digit", year: "numeric" });
    }

    if (format === "time") {
      return dateTime.toLocaleString(DateTime.TIME_SIMPLE);
    }

    if (format === "dateTime") {
      return dateTime.toLocaleString(DateTime.DATETIME_MED);
    }
  }

  return dateTime;
};

/**
 * Generates a random Globally Unique Identifier
 * @param {string} template optional format of the GUID to be returned. Xs and Ys are replaced with a random hexadecimal digit
 * @returns by default, a GUID string made of lowercase hexadecimal digits in groups of 8, 4, 4, 4, and 12 digits separated by hyphens
 */
export const generateGuid = (template) => {
  const guidTemplate = template || "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx";

  return guidTemplate.replace(/[xy]/g, (c) => {
    const randomHex = (Math.random() * 16) | 0;
    const value = c === "x" ? randomHex : randomHex && 0x3 | 0x8;

    return value.toString(16);
  });
};

/**
 * Returns any custom error messages set in the backend or the default status message if nothing found
 * @param {object} exception the exception with the response and errors
 * @returns optimal error message
 */
export const getBadResponseErrorMessage = (exception) => {
  const data = exception.response?.data;

  if (!data) {
    if (exception.message) {
      return exception.message; //Default status message like "Internal Server Error"
    } else {
      return "Unknown error"; //This should never happen
    }
  }

  if (typeof data === "string") {
    return data; //return StatusCode(500, "error")
  } else if (data.error) {
    return data.error; //return StatusCode(500, new {Error = "error"})
  } else if (data.title) {
    //Expected endpoint data is not matching payload
    if (data.title === "One or more validation errors occurred.") {
      const errorCount = Object.values(data.errors)?.length;

      return `Bad payload. ${errorCount} validation error(s) occurred. Check the console for the list of errors`;
    } else {
      return data.title; //Not sure what else has this title format
    }
  } else {
    return "Unknown error"; //This should never happen
  }
};

/**
 * Takes in an array of objects and compare them with each other
 * @param {Array} objects
 * @returns true if objects are the same or false if they are not
 */
export const compareObjects = (objects) => {
  if (objects?.length < 2) {
    return true;
  }

  let isEqual = true;
  const firstObjectString = JSON.stringify(objects[0]);

  for (let i = 1; i < objects.length; i++) {
    if (!isEqual) {
      continue;
    }

    const nextObjectString = JSON.stringify(objects[i]);

    if (firstObjectString !== nextObjectString) {
      isEqual = false;
    }
  }

  return isEqual;
};

export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  return emailRegex.test(email);
};

export const debugLog = (title, body) => {
  if (process.env.REACT_APP_DEBUG === "true") {
    console.log(title, body);
  }
};
